import { useCallback } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type Board, type Clip, type ShortUrlResponse } from '@air/api/types';
import { Modal, ModalDescription } from '@air/primitive-modal';

import { RegenerateShareLinkButtons } from '~/components/SharePrivateContentModal/RegenerateLink/RegenerateShareLinkButtons';
import { ShareModalHeader } from '~/components/SharePrivateContentModal/ShareModalHeader';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useRegenerateShortIdForClip } from '~/swr-hooks/shortUrl/useRegenerateShortIdForClip';

export type SharePrivateClipRegenerateProps = {
  clip: Pick<Clip, 'id' | 'assetVersionCount'>;
  trackLocation: AirActionTrackingLocation;
  shortIdInfo: ShortUrlResponse;
  onRegenerated: () => void;
  onCancel: () => void;
  onManageNotificationsClick: () => void;
  boardContext: Board['id'] | undefined;
};

export const SharePrivateClipRegenerate = ({
  clip,
  trackLocation,
  shortIdInfo,
  onClose,
  onCancel,
  onRegenerated,
  boardContext,
}: AirModalProps<SharePrivateClipRegenerateProps>) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { regenerateShortIdForClip } = useRegenerateShortIdForClip();

  const onRegenerate = useCallback(() => {
    const workspaceId = currentWorkspace?.id;
    if (!workspaceId) {
      throw new Error('No workspace id');
    }
    regenerateShortIdForClip.mutate(
      {
        workspaceId,
        clipId: clip.id,
        shortId: shortIdInfo.id,
        permissions: shortIdInfo.permissions,
        boardContext,
        trackLocation,
      },
      {
        onSuccess: () => {
          onClose();
          onRegenerated();
        },
      },
    );
  }, [
    currentWorkspace?.id,
    boardContext,
    clip.id,
    onClose,
    onRegenerated,
    regenerateShortIdForClip,
    shortIdInfo.id,
    shortIdInfo.permissions,
    trackLocation,
  ]);

  return (
    <Modal isOpen onDismiss={onClose}>
      <ShareModalHeader onClose={onClose} title="Generate new link?" />
      <ModalDescription>
        This will break the current URL and people with the old link will no longer have access to the asset. Are you
        sure you want to generate a new URL?
      </ModalDescription>
      <RegenerateShareLinkButtons
        isLoading={regenerateShortIdForClip.isPending}
        onRegenerate={onRegenerate}
        onClose={() => {
          onClose();
          onCancel();
        }}
      />
    </Modal>
  );
};
